import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../utils/auth';

const FeaturesContext = createContext(null);

export function FeaturesProvider({ children }) {
  const [features, setFeatures] = useState({});
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadFeatures = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setUserInfo(null);
      setFeatures({});
      setLoading(false);
      return;
    }

    try {
      // First try to get from localStorage (set during login)
      const storedFeatures = localStorage.getItem('features');
      if (storedFeatures) {
        setFeatures(JSON.parse(storedFeatures));
      }

      // Then fetch fresh from API
      const data = await apiRequest('/api/auth/me');
      setUserInfo(data);
      setFeatures(data.features || {});
      localStorage.setItem('features', JSON.stringify(data.features || {}));
    } catch (error) {
      console.error('Failed to load features:', error);
      // Use stored features as fallback
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  // Listen for storage changes (login/logout in other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token') {
        loadFeatures();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [loadFeatures]);

  // Check if a feature is enabled (default to true if not specified)
  const isFeatureEnabled = (featureKey, defaultValue = true) => {
    if (Object.keys(features).length === 0) {
      return defaultValue; // No features configured = all enabled
    }
    return features[featureKey] !== false; // Only false disables, undefined = enabled
  };

  // Refresh features (call after login or when needed)
  const refreshFeatures = useCallback(async () => {
    setLoading(true);
    await loadFeatures();
  }, [loadFeatures]);

  return (
    <FeaturesContext.Provider value={{ 
      features, 
      userInfo, 
      loading, 
      isFeatureEnabled,
      refreshFeatures,
      isSSOUser: userInfo?.is_sso || localStorage.getItem('is_sso') === 'true'
    }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures() {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
}

// HOC for feature-gated routes
export function withFeature(WrappedComponent, featureKey) {
  return function FeatureGatedComponent(props) {
    const { isFeatureEnabled, loading } = useFeatures();
    
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2540]"></div>
        </div>
      );
    }

    if (!isFeatureEnabled(featureKey)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8 max-w-md">
            <div className="text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Module Not Available</h1>
            <p className="text-gray-600 mb-4">
              The "{featureKey}" module is not enabled for your account.
            </p>
            <p className="text-sm text-gray-500">
              Please contact your administrator to enable this feature.
            </p>
          </div>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
}
